/**
 * Anubis-style proof-of-work anti-bot gate (client-side edition).
 *
 * Real Anubis (Techaro) is a server-side proxy: the server issues a challenge
 * and refuses to serve content until the client returns a nonce whose
 * SHA-256 meets the difficulty target. On static hosting there is no server
 * to enforce that, so this module reproduces the *client* half: a PoW
 * challenge that must be solved before the app shell mounts. It is a
 * speed bump against naive scrapers and drive-by bots, not a hard wall —
 * a determined bot can skip it. Documented in CLAUDE.md.
 *
 * Algorithm mirrors Anubis: response = SHA-256(challenge || nonce),
 * accepted when the hex digest has DIFFICULTY leading zero nibbles.
 */
import { sha256 } from '@noble/hashes/sha2.js';
import { utf8ToBytes, bytesToHex, randomBytes } from '@noble/hashes/utils.js';

export const DIFFICULTY = 4; // leading zero nibbles — ~65k hashes expected, ~1-3s on a phone
export const PASS_TTL_MS = 7 * 24 * 3600 * 1000; // Anubis default cookie lifetime: one week
const LS_POW = 'relatos.pow';

interface PowPass { challenge: string; nonce: number; ts: number }

const digest = (challenge: string, nonce: number) =>
  bytesToHex(sha256(utf8ToBytes(challenge + nonce)));

export const meets = (hash: string, difficulty = DIFFICULTY) =>
  hash.slice(0, difficulty).split('').every(c => c === '0');

/** Solve a PoW challenge. Yields to the event loop every 512 tries. */
export async function solvePow(challenge: string, onProgress?: (tries: number) => void): Promise<number> {
  let nonce = 0;
  for (;;) {
    if (meets(digest(challenge, nonce))) return nonce;
    nonce++;
    if (nonce % 512 === 0) {
      onProgress?.(nonce);
      await new Promise(r => setTimeout(r, 0));
    }
  }
}

/** Verify a stored pass: one hash, plus expiry. */
export function verifyPass(pass: PowPass): boolean {
  if (typeof pass?.challenge !== 'string' || typeof pass?.nonce !== 'number') return false;
  if (Date.now() - pass.ts > PASS_TTL_MS) return false;
  return meets(digest(pass.challenge, pass.nonce));
}

export function loadPass(): boolean {
  try {
    const p = JSON.parse(localStorage.getItem(LS_POW) || 'null');
    return p && verifyPass(p);
  } catch { return false; }
}

/** Run the full gate: returns when a valid pass exists (solving if needed). */
export async function ensurePass(onProgress?: (tries: number) => void): Promise<void> {
  if (loadPass()) return;
  const challenge = bytesToHex(randomBytes(16));
  const nonce = await solvePow(challenge, onProgress);
  const pass: PowPass = { challenge, nonce, ts: Date.now() };
  localStorage.setItem(LS_POW, JSON.stringify(pass));
}
